/**
 * Search a document's OCR text when the PDF's own text layer has none.
 *
 * The viewer's search box (Apryse) reads only the text layer embedded in
 * the PDF. A faxed or scanned record has no such layer — or a banner-only
 * one — so searching "ackerman" in a 25-page pulmonology fax returned
 * "0 results" while the name sat in plain sight on page 9 (Stanton,
 * 2026-09-10). The pipeline's extracted markdown for the same document
 * is rich and carries `## Page N` markers, so a miss in the viewer can be
 * answered from that text: which pages hold the words, so the reader can
 * be taken there. No highlight box is possible without a text layer; the
 * page is the deliverable.
 *
 * Pure helpers here (unit-tested); the viewer wiring lives in
 * `components/Document/Document.js`.
 */

// Same two marker shapes the backend locator reads
// (`doc_pipeline/page_locator.py`): `## Page 12` from dms-pipeline and the
// dashed `--- Page 12 ---` from older artifacts.
const PAGE_MARKER_RE = /^[ \t]*(?:#{1,6}[ \t]*Page[ \t]+(\d+)\b.*|-{2,}[ \t]*Page[ \t]+(\d+)[ \t]*-{2,})[ \t]*$/gim;

/** Words only, lowercased, single-spaced — survives punctuation and line breaks. */
export function normalizeText(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * `[{ page, text }]` for one document's markdown, `text` normalized.
 * Empty when the markdown carries no page markers.
 */
export function splitMarkdownIntoPages(markdown) {
  const source = String(markdown || '');
  const markers = [];
  PAGE_MARKER_RE.lastIndex = 0;
  let match;
  while ((match = PAGE_MARKER_RE.exec(source)) !== null) {
    markers.push({
      page: parseInt(match[1] || match[2], 10),
      start: match.index,
      contentStart: match.index + match[0].length,
    });
  }
  if (!markers.length) return [];
  return markers.map((marker, i) => {
    const end = i + 1 < markers.length ? markers[i + 1].start : source.length;
    return { page: marker.page, text: normalizeText(source.slice(marker.contentStart, end)) };
  });
}

/**
 * Pages (ascending, unique) whose OCR text contains `query`.
 *
 * `wholeWord` mirrors the viewer's option; case is always folded because
 * normalisation lowercases both sides. Returns `[]` for a query shorter
 * than two characters — one letter would match every page.
 */
export function findPagesForQuery(pages, query, { wholeWord = false } = {}) {
  const needle = normalizeText(query);
  if (needle.length < 2) return [];
  const test = wholeWord
    ? (text) => new RegExp(`(^|\\s)${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`).test(text)
    : (text) => text.includes(needle);
  const seen = new Set();
  const out = [];
  for (const { page, text } of pages) {
    if (!seen.has(page) && test(text)) {
      seen.add(page);
      out.push(page);
    }
  }
  return out.sort((a, b) => a - b);
}

/**
 * Human sentence for the banner: "Found on page 9" / "Found on pages 9, 14
 * and 27" / "Found on 12 pages, first on page 9".
 */
export function describePages(pages) {
  if (!pages || !pages.length) return '';
  if (pages.length === 1) return `Found on page ${pages[0]}`;
  if (pages.length <= 5) {
    const head = pages.slice(0, -1).join(', ');
    return `Found on pages ${head} and ${pages[pages.length - 1]}`;
  }
  return `Found on ${pages.length} pages, first on page ${pages[0]}`;
}
